import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import PageHeader from '@/components/system/PageHeader';
import SectionCard from '@/components/system/SectionCard';
import { AppBreadcrumb } from '@/components/system/AppBreadcrumb';
import { ContextBackButton } from '@/components/system/ContextBackButton';
import { CHART_TOOLTIP_STYLE } from '@/components/system/ChartTooltip';
import { InteractiveChart } from '@/components/system/InteractiveChart';
import { InsightList } from '@/components/system/InsightCard';
import { DiagnosticPanel } from '@/components/system/DiagnosticPanel';
import { RecommendationPanel } from '@/components/system/RecommendationPanel';
import { generateInsights } from '@/lib/insightRules';
import { generateDiagnostic } from '@/lib/diagnosticEngine';
import { generateRecommendations } from '@/lib/recommendationEngine';
import type { AnalyticalInsight } from '@/types/insights';
import type { RootCauseAnalysis } from '@/types/diagnostics';
import type { OperationalRecommendation } from '@/types/recommendations';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import {
  Building2, AlertTriangle, TrendingUp, Activity, Lightbulb,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import RiskUOMap from '@/components/risk/RiskUOMap';
import RiskUORanking from '@/components/risk/RiskUORanking';

// ─── Types ──────────────────────────────────────────────────────────
interface UOHeatmapRow {
  uo_id: string;
  uo_nome: string;
  cluster_id: string;
  cluster_nome: string;
  contract_id: string;
  contrato_nome: string;
  classificacao_risco: string;
  score_risco_uo: number | null;
  total_inspecoes: number;
  total_nao_conformidades: number;
  total_riscos_imediatos: number;
  progresso: number;
  orcamento: number;
  densidade_nao_conformidade: number;
  densidade_risco_imediato: number;
  nivel_calor: string;
  municipality_code: string | null;
  cidade: string | null;
  lat: number | null;
  lng: number | null;
}

const CALOR_COLORS: Record<string, string> = {
  critico: '#B42318',
  alto: '#E5780B',
  moderado: '#C8A100',
  baixo: '#2E7D6B',
};

// ─── Main Component ─────────────────────────────────────────────────
const RiskUODashboard: React.FC = () => {
  const [data, setData] = useState<UOHeatmapRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedUOId, setSelectedUOId] = useState<string | null>(null);
  const [nivelFilter, setNivelFilter] = useState<string | null>(null);
  const [activeDiagnostic, setActiveDiagnostic] = useState<RootCauseAnalysis | null>(null);
  const [activeRecommendations, setActiveRecommendations] = useState<OperationalRecommendation[]>([]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const { data: rows, error } = await supabase
        .from('vw_risk_uo_heatmap')
        .select('*')
        .order('score_risco_uo', { ascending: false, nullsFirst: false });
      if (!error && rows) setData(rows as UOHeatmapRow[]);
      setLoading(false);
    };
    fetchData();
  }, []);

  // KPI aggregations
  const kpis = useMemo(() => {
    if (data.length === 0) return null;
    const withScore = data.filter(r => r.score_risco_uo != null);
    const maxRiskUO = withScore.length > 0
      ? withScore.reduce((a, b) => (a.score_risco_uo! > b.score_risco_uo! ? a : b))
      : null;
    const criticos = data.filter(r => r.nivel_calor === 'critico');
    const totalNC = data.reduce((s, r) => s + r.total_nao_conformidades, 0);
    return { total: data.length, maxRiskUO, criticos: criticos.length, totalNC };
  }, [data]);

  // Chart data
  const NIVEL_KEYS = ['critico', 'alto', 'moderado', 'baixo'];
  const heatmapChart = useMemo(() => {
    const counts: Record<string, number> = { critico: 0, alto: 0, moderado: 0, baixo: 0 };
    for (const r of data) counts[r.nivel_calor] = (counts[r.nivel_calor] || 0) + 1;
    return [
      { name: 'Crítico', value: counts.critico, color: CALOR_COLORS.critico, key: 'critico' },
      { name: 'Alto', value: counts.alto, color: CALOR_COLORS.alto, key: 'alto' },
      { name: 'Moderado', value: counts.moderado, color: CALOR_COLORS.moderado, key: 'moderado' },
      { name: 'Baixo', value: counts.baixo, color: CALOR_COLORS.baixo, key: 'baixo' },
    ];
  }, [data]);

  // ── Insights from rule engine
  const uoInsights = useMemo(() => {
    if (data.length === 0) return [];
    const uoMap = new Map<string, { uo_id: string; uo_nome: string; nivel_risco: string; score_medio: number; total_contratos: number }>();
    for (const r of data) {
      if (!uoMap.has(r.uo_id)) {
        uoMap.set(r.uo_id, { uo_id: r.uo_id, uo_nome: r.uo_nome || r.cidade || r.uo_id, nivel_risco: r.nivel_calor, score_medio: r.score_risco_uo ?? 0, total_contratos: 1 });
      } else {
        uoMap.get(r.uo_id)!.total_contratos++;
      }
    }
    return generateInsights({ uos: Array.from(uoMap.values()) });
  }, [data]);

  const uoDiagnosticContext = useMemo(() => ({
    uos: data.length === 0 ? [] : (() => {
      const uoMap = new Map<string, { uo_id: string; uo_nome: string; nivel_risco: string; score_medio: number; total_contratos: number }>();
      for (const r of data) {
        if (!uoMap.has(r.uo_id)) {
          uoMap.set(r.uo_id, { uo_id: r.uo_id, uo_nome: r.uo_nome || r.cidade || r.uo_id, nivel_risco: r.nivel_calor, score_medio: r.score_risco_uo ?? 0, total_contratos: 1 });
        } else {
          uoMap.get(r.uo_id)!.total_contratos++;
        }
      }
      return Array.from(uoMap.values());
    })(),
  }), [data]);

  const handleInsightClick = useCallback((insight: AnalyticalInsight) => {
    const diag = generateDiagnostic(insight, uoDiagnosticContext);
    setActiveDiagnostic(diag);
    if (diag) {
      setActiveRecommendations(generateRecommendations(insight, diag, uoDiagnosticContext));
    } else {
      setActiveRecommendations([]);
    }
  }, [uoDiagnosticContext]);

  const handleBarClick = useCallback((_: any, index: number) => {
    const key = NIVEL_KEYS[index];
    setNivelFilter(prev => prev === key ? null : key);
  }, []);

  // Filtered data for ranking when bar is clicked
  const filteredData = useMemo(() => {
    if (!nivelFilter) return data;
    return data.filter(r => r.nivel_calor === nivelFilter);
  }, [data, nivelFilter]);

  // Sync: scroll table to selected UO
  useEffect(() => {
    if (!selectedUOId) return;
    const el = document.querySelector(`[data-uo-id="${selectedUOId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [selectedUOId]);

  const handleSelectUO = useCallback((uoId: string | null) => {
    setSelectedUOId(prev => prev === uoId ? null : uoId);
  }, []);

  if (loading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28" />)}
        </div>
        <Skeleton className="h-72" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <AppBreadcrumb items={[
        { label: 'Empreendimentos', href: '/empreendimentos' },
        { label: 'Inteligência de Risco', href: '/empreendimentos/inteligencia' },
        { label: 'Risco por UO' },
      ]} />
      <ContextBackButton to="/empreendimentos/inteligencia" label="Inteligência de Risco" />
      <PageHeader
        title="Risco por Unidade Operacional"
        subtitle="Visão consolidada de risco por UO com localização geográfica"
      />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SectionCard title="UOs Monitoradas">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10"><Building2 className="h-5 w-5 text-primary" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total</p>
              <p className="text-2xl font-bold">{kpis?.total ?? 0}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="UOs Críticas">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-destructive/10"><AlertTriangle className="h-5 w-5 text-destructive" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Nível Crítico</p>
              <p className="text-2xl font-bold text-destructive">{kpis?.criticos ?? 0}</p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Maior Risco">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-orange-500/10"><Activity className="h-5 w-5 text-orange-500" /></div>
            <div>
              <p className="text-sm font-bold truncate max-w-[140px]" title={kpis?.maxRiskUO?.uo_nome}>
                {kpis?.maxRiskUO?.uo_nome ?? '—'}
              </p>
              <p className="text-xs text-muted-foreground">
                Score: {kpis?.maxRiskUO?.score_risco_uo ?? '—'}
              </p>
            </div>
          </div>
        </SectionCard>

        <SectionCard title="Não-Conformidades">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><TrendingUp className="h-5 w-5 text-amber-600" /></div>
            <div>
              <p className="text-xs text-muted-foreground">Total NC</p>
              <p className="text-2xl font-bold">{kpis?.totalNC ?? 0}</p>
            </div>
          </div>
        </SectionCard>
      </div>

      {/* Map + Distribution side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <SectionCard title="Mapa de Risco por Cidade" className="lg:col-span-2">
          <div className="h-[400px]">
            <RiskUOMap
              data={data}
              selectedUOId={selectedUOId}
              onSelectUO={handleSelectUO}
            />
          </div>
        </SectionCard>

        <SectionCard title="Distribuição por Nível">
          <InteractiveChart
            data={heatmapChart}
            hint="Clique em uma barra para filtrar o ranking"
            activeFilter={nivelFilter}
            onClearFilter={() => setNivelFilter(null)}
            height={370}
          >
            {(chartProps) => (
              <BarChart data={heatmapChart} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" className="opacity-30" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={80} tick={{ fontSize: 12 }} />
                <Tooltip {...chartProps.tooltipProps} />
                <Bar dataKey="value" radius={[0, 4, 4, 0]} onClick={handleBarClick} className="cursor-pointer">
                  {heatmapChart.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={entry.color}
                      {...chartProps.getCellProps(entry.key)}
                    />
                  ))}
                </Bar>
              </BarChart>
            )}
          </InteractiveChart>
        </SectionCard>
      </div>

      {/* Ranking Table */}
      <SectionCard title={nivelFilter ? `Ranking — ${heatmapChart.find(h => h.key === nivelFilter)?.name || 'Filtrado'}` : 'Ranking de Unidades Operacionais'}>
        {nivelFilter && (
          <button onClick={() => setNivelFilter(null)} className="text-xs text-primary hover:underline mb-2">
            ✕ Limpar filtro
          </button>
        )}
        <RiskUORanking
          data={filteredData}
          search={search}
          onSearchChange={setSearch}
          selectedUOId={selectedUOId}
          onSelectUO={handleSelectUO}
        />
      </SectionCard>

      {/* ── Insights do Sistema ──────────────────────────── */}
      {uoInsights.length > 0 && (
        <SectionCard title="Insights do Sistema" icon={Lightbulb}>
          <InsightList insights={uoInsights} maxItems={4} compact onInsightClick={handleInsightClick} />
        </SectionCard>
      )}

      {/* ── Diagnóstico de Causa Raiz ────────────────────── */}
      {activeDiagnostic && (
        <DiagnosticPanel
          diagnostic={activeDiagnostic}
          onClose={() => { setActiveDiagnostic(null); setActiveRecommendations([]); }}
        />
      )}

      {/* ── Recomendações Operacionais ────────────────────── */}
      {activeRecommendations.length > 0 && (
        <RecommendationPanel
          recommendations={activeRecommendations}
          onClose={() => setActiveRecommendations([])}
        />
      )}
    </div>
  );
};

export default RiskUODashboard;
